import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DiaryRootState } from 'appRoot/rootReducer';
import { IBooking } from 'shared-types/SharedTypes';
import { useAppDispatch } from 'appRoot/Store';
import {FloorplanTableOverlayStatus} from "floorplan-v2/app/services/floorplanNonReduxState/types";
import FloatingBookingCard from './index';
import style from './style.module.scss';
import { IconTypes } from 'external-services/icons-service/icons.types';
import IconsService from 'external-services/icons-service/icons.service';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import ActionButton from 'design-v2/common/ActionButton';
import {FloorplanV2State} from "floorplan-v2/app/appRoot/rootReducer";
import FloorplanNonReduxState from "floorplan-v2/app/services/floorplanNonReduxState";
import {IFastBookingCard} from "floorplan-v2/app/services/floorplanNonReduxState/types";

const useStyles = makeStyles({
  root: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 10,
  },
});

const CARD_W = 300;

const LoadingIcon = () => {
  const classes = useStyles();
  return (
    <div data-testid="loaderIcon"
      className={classes.root}>
      <CircularProgress />
    </div>
  );
};

const BookingCardContent = (props: {
  tableBookings: IBooking[];
  tableOverlayStatus: FloorplanTableOverlayStatus;
}) => {
  const { tableBookings, tableOverlayStatus } = props;
  const loadLimit: number = 2;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [bookings, setBookings] = useState<IBooking[]>();
  const [loadCount, setLoadCount] = useState<number>(0);
  const [loadDisabled, setLoadDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (
      tableBookings &&
      tableBookings.length === 1 &&
      tableOverlayStatus === FloorplanTableOverlayStatus.Normal
    ) {
      setBookings(tableBookings);
      setLoadDisabled(true);
      setLoadCount(1);
      setIsLoading(false);
    } else {
      setLoadCount(0);
      setBookings([]);
    }
  }, [tableBookings]);

  const handleLoadMore = () => {
    if (loadCount * loadLimit < tableBookings.length) {
      setIsLoading(true);
      setTimeout(async () => {
        const tempBookings = tableBookings;
        setBookings(tempBookings.slice(0, loadLimit * (loadCount + 1)));
        setLoadDisabled((loadCount + 1) * loadLimit >= tempBookings.length);
        setLoadCount(loadCount + 1);
        setIsLoading(false);
      }, 300);
    } else {
      setLoadDisabled(true);
    }
  };

  const BookingCardMemo = useMemo(() => {
    return bookings?.map((booking: IBooking) => (
      <div className={style.cardRoot} key={booking._id}>
        <FloatingBookingCard booking={booking} />
      </div>
    ));
  }, [bookings]);

  return (
    <>
      {/* @todo: Check if this functionality is meant to be no longer visible (shared tables from UI-1064) */}
      {!loadDisabled && loadCount === 0 && bookings && (
        <div
          className={style.clickView}
          onClick={() => {
            handleLoadMore();
          }}
        >
          Click here to view bookings
        </div>
      )}
      <div className={style.cardList}>
        {BookingCardMemo}

        {/* @todo: Check if this functionality is meant to be no longer visible (shared tables from UI-1064) */}
        {!loadDisabled && loadCount !== 0 && (
          <div className={style.loadButton}>
            <ActionButton
              variant={'secondary'}
              size={'small'}
              maxWidth={400}
              text={'Show more'}
              handleClick={() => handleLoadMore()}
            />
          </div>
        )}
        {isLoading && (
          <div className={style.loadingContainer}>
            <LoadingIcon />
          </div>
        )}
      </div>
    </>
  );
};


const FloatingBookingCardContainer = () => {
  const dispatch = useAppDispatch();

  // prettier-ignore
  const draggedBooking = useSelector((state: FloorplanV2State) => state.floorplanTableDragReducer.draggedBooking);
  const showBlockoutByService = useSelector((state: DiaryRootState) => state.rootDisplayReducer.showBlockoutByService);
  const showBlockout = useSelector((state: DiaryRootState) => state.rootDisplayReducer.showBlockout);
  const showBlockoutOnFp = useSelector((state: DiaryRootState) => state.rootDisplayReducer.showBlockoutOnFp);
  const showBlockoutTimePicker = useSelector((state: DiaryRootState) => state.rootDisplayReducer.showBlockoutTimePicker);
  const showBlockoutBySectionOnFp = useSelector((state: DiaryRootState) => state.rootDisplayReducer.showBlockoutBySectionOnFp);
  const activeAddBlockoutsByTable = useSelector((state: DiaryRootState) => state.floorplanBlockoutsReducer.activeAddBlockoutsByTable);

  const [fastBookingCard, setFastBookingCard] = useState<IFastBookingCard>(null);

  useEffect(() => {
    FloorplanNonReduxState.setFastBookingCardCallback((value: IFastBookingCard) => {
      setFastBookingCard(value);
    });
  }, []);

  useEffect(() => {
    if (activeAddBlockoutsByTable) {
      // stops FloatingBookingCard accidentally showing up when activating blockouts
      setFastBookingCard(null);
    }
  }, [activeAddBlockoutsByTable]);

  if (
    draggedBooking || // don't show hover when dragging
    !fastBookingCard ||
    showBlockoutByService || showBlockout || showBlockoutOnFp || showBlockoutTimePicker || showBlockoutBySectionOnFp // all blockout modals not show
  ) {
    return <></>;
  }

  const BookingCardConflictTableTitle = () => {
    const WarningIcon = IconsService.getIcon(IconTypes.warning);
    if (fastBookingCard.tableOverlayStatus !== FloorplanTableOverlayStatus.Conflict) return <></>;
    return (
      <div
        // className={classNames({
        //   [style.conflictTitleContainer]: false, // set as true when conflict booking will be display on overlay
        // })}
      >
        <div className={style.conflictTitleWarningContainer}>
          <WarningIcon className={style.conflictTitleWarningIcon} />
          <span
            className={style.conflictTitle}
          >{`Booking conflicts (${(fastBookingCard.bookings.length - 1).toString()})`}</span>
        </div>
      </div>
    );
  };

  const BookingCardSharedTableTitle = () => {
    const GroupIcon = IconsService.getIcon(IconTypes.group);
    if (fastBookingCard.tableOverlayStatus !== FloorplanTableOverlayStatus.Shared) {
      return <></>;
    }
    return (
      <div
        className={classNames({
          [style.sharedTitleContainer]: true,
        })}
      >
        <div className={style.sharedTitleLayoutContainer}>
          <GroupIcon />
          <span
            className={style.conflictTitle}
          >{`Shared table bookings (${fastBookingCard.bookings.length.toString()})`}</span>
        </div>
      </div>
    );
  };


  return (
    <div style={{
      position: 'absolute',
      top: fastBookingCard.position.y,
      left: fastBookingCard.position.x - (CARD_W/2),
    }}>
    <div
      id={'floating-booking-card'}
      data-testid={'floating-booking-card'}
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        zIndex: 70, // this one needs to lower than the backdrop of booking dialog(80)
        background: 'white',
        boxShadow: 'rgba(0, 0, 0, 0.25) 0px 2px 8px 0px',
        width: CARD_W
      }}
    >
      {BookingCardConflictTableTitle()}
      {BookingCardSharedTableTitle()}
      {fastBookingCard.tableOverlayStatus === FloorplanTableOverlayStatus.Normal && !!fastBookingCard.bookings.length &&  (
        <BookingCardContent
          tableBookings={fastBookingCard.bookings}
          tableOverlayStatus={fastBookingCard.tableOverlayStatus}
        />
      )}
    </div>
    </div>
  );
};

export default FloatingBookingCardContainer;
